import _ from 'lodash';
import { createSelector } from 'reselect';

import {
  getComponentProps,
  getEntityData,
  getEntityDefinition,
  getFormModel,
  getPrimaryKeysMap,
  getTranslations,
  getWorkflowInfo,
} from './common.selectors';
import { getSsContext } from './form-manager.selectors';
import { isTrue } from '../../utils/commonUtils';
import { SS_CONTROL_TYPE } from '../constants/form-manager.constants';
import { evaluateConditions } from '../utils/evaluation.utils';
import {
  createGridColumns,
  getDataPath,
  getPropType,
  getRelationShipKey,
} from '../utils/form-manager.utils';
import {
  checkDevGridError,
  filterChildAttachmentRows,
  filterRowsByMappingValues,
  getGridArrIndexForRowFilter,
  getGridRowsPropertiesMapBasedOnFlag,
  getIndexesForDataMember,
  translateRows,
} from '../utils/runtime.utils';

const getRowsFromEntityData = createSelector(
  [getEntityData, getEntityDefinition, getComponentProps],
  (entityData, entityDefinition, { entityName, index, isParentlessTable }) => {
    if (isParentlessTable) {
      return [];
    }

    if (entityName === 'V_ENU_NONPERSIS_USER_DATA') {
      const entityType = Object.keys(entityDefinition)[0];
      const userData = entityData[entityType].V_ENU_NONPERSIS_USER_DATA;
      return userData ? [userData] : [];
    }

    const dataPath = getDataPath(entityName, index, entityDefinition);
    // if the last element is an index or undefined, remove it and get only the array
    const entityPath =
      _.isNumber(_.last(dataPath)) || _.isNil(_.last(dataPath)) ? dataPath.slice(0, -1) : dataPath;

    return _.get(entityData, entityPath, []);
  }
);

const getGridRows = createSelector(
  [getRowsFromEntityData, getPrimaryKeysMap, getComponentProps],
  (rows, primaryKeysMap, { entityName }) => {
    if (!Array.isArray(rows)) {
      return [];
    }

    const primaryKey = primaryKeysMap[entityName];

    return rows.map((row, i) => {
      const rowKey = row[primaryKey] ?? row._newRowId;

      checkDevGridError(rowKey, entityName);

      return {
        ...row,
        index: i,
        key: rowKey,
      };
    });
  }
);

const getRowsForParentlessTable = createSelector(
  [getEntityData, getEntityDefinition, getPrimaryKeysMap, getComponentProps],
  (entityData, entityDefinition, primaryKeysMap, { entityName, isParentlessTable }) => {
    if (!isParentlessTable) {
      return [];
    }

    const entityType = Object.keys(entityDefinition)[0];
    const indexes = getIndexesForDataMember(entityData, entityType, entityName);
    const primaryKey = primaryKeysMap[entityName];

    return indexes.map((index) => {
      const path = getDataPath(entityName, index, entityDefinition);
      const row = _.get(entityData, path);
      const rowKey = row[primaryKey] ?? row._newRowId;

      checkDevGridError(rowKey, entityName);

      return { ...row, gridArrIndex: index, key: rowKey };
    });
  }
);

export const getFilteredRows = createSelector(
  [getGridRows, getRowsForParentlessTable, getSsContext, getComponentProps],
  (
    baseTableRows,
    parentlessTableRows,
    ssContext,
    {
      id,
      columns = [],
      translateString,
      mappingValues,
      targetControlId,
      isParentlessTable,
      parentEntityName,
      parentRowId,
    }
  ) => {
    const { gridArrIndex, formModel } = ssContext;
    const filterConditions = formModel[id]?.filter?.Conditions;
    const isChildAttachment = formModel[id]?.gridType === 'ChildAttachment';
    let rows = isParentlessTable ? parentlessTableRows : baseTableRows;

    if (isChildAttachment && parentEntityName && parentRowId) {
      rows = filterChildAttachmentRows(rows, parentEntityName, parentRowId);
    }

    if (mappingValues && id === targetControlId) {
      rows = filterRowsByMappingValues(rows, mappingValues);
    }

    if (!rows.length || _.isNil(filterConditions)) {
      return translateRows({ rows, columns, translateString });
    }

    const filterArray = rows.map((row) =>
      evaluateConditions({
        conditions: filterConditions,
        ssContext: {
          ...ssContext,
          gridArrIndex: isParentlessTable
            ? row.gridArrIndex
            : getGridArrIndexForRowFilter(gridArrIndex, row.index),
        },
        datasourceRow: row,
      })
    );

    const filteredRows = rows.filter(
      (row, i) => filterArray[i] || row.ContainerInstanceIdForHandMadeNewObject === id
    );

    return translateRows({ rows: filteredRows, columns, translateString });
  }
);

/**
 * returns all rows with isFilteredOut flag based on table filters
 */
export const getVirtualRows = createSelector(
  [getGridRows, getRowsForParentlessTable, getSsContext, getComponentProps],
  (baseTableRows, parentlessTableRows, ssContext, { id, isParentlessTable }) => {
    const { gridArrIndex, formModel } = ssContext;
    const filterConditions = formModel[id].filter?.Conditions;
    const rows = isParentlessTable ? parentlessTableRows : baseTableRows;

    if (!rows.length || _.isNil(filterConditions)) {
      return rows.map((r) => ({ ...r, isFilteredOut: false }));
    }

    const filterArray = rows.map((row) =>
      evaluateConditions({
        conditions: filterConditions,
        ssContext: {
          ...ssContext,
          gridArrIndex: isParentlessTable
            ? row.gridArrIndex
            : getGridArrIndexForRowFilter(gridArrIndex, row.index),
        },
        datasourceRow: row,
      })
    );

    return rows.map((r, i) => ({
      ...r,
      isFilteredOut: !filterArray[i] && r.ContainerInstanceIdForHandMadeNewObject !== id,
    }));
  }
);

export const getFilteredRowsCount = createSelector([getFilteredRows], (rows) => rows.length);

export const getEvaluatedTableRowsProperties = createSelector(
  [getFilteredRows, getSsContext, getComponentProps],
  (rows, ssContext, { id, prop, isParentlessTable }) => {
    const { gridArrIndex, formModel } = ssContext;
    const result = formModel[id][prop];
    const filterArrayMap = {};

    if (_.isNil(result)) {
      return getGridRowsPropertiesMapBasedOnFlag(rows, false, isParentlessTable);
    }

    if (['string', 'boolean'].includes(typeof result)) {
      return getGridRowsPropertiesMapBasedOnFlag(rows, isTrue(result), isParentlessTable);
    }

    rows.forEach((row) => {
      const key = isParentlessTable ? row.gridArrIndex : row.index;
      const ssContextGridArrIndex = isParentlessTable
        ? row.gridArrIndex
        : getGridArrIndexForRowFilter(gridArrIndex, row.index);

      filterArrayMap[key] = evaluateConditions({
        conditions: result.Conditions,
        ssContext: {
          ...ssContext,
          gridArrIndex: ssContextGridArrIndex,
        },
        datasourceRow: row,
      });
    });

    return filterArrayMap;
  }
);

const getColumnsModel = createSelector(
  [getFormModel, getComponentProps],
  (formModel, { childrenNodes }) =>
    childrenNodes
      .filter((node) => formModel[node].controlType === SS_CONTROL_TYPE.GRID_COLUMN)
      .map((node) => {
        const element = formModel[node];
        const childId = element.childrenNodes?.[0];

        const hasDataSourceChild = [
          SS_CONTROL_TYPE.DROP_DOWN,
          SS_CONTROL_TYPE.RADIO_BUTTON_LIST,
        ].includes(formModel[childId]?.controlType);

        return {
          ...element,
          hasDataSourceChild,
          childFieldName: formModel[childId]?.fieldName ?? '',
          childEntityName: formModel[childId]?.entityName ?? '',
          childControlType: formModel[childId]?.controlType,
        };
      })
);

export const getGridColumns = createSelector(
  [getColumnsModel, getEntityDefinition, getTranslations],
  (columnsModels, entityDefinition, translations) =>
    columnsModels.map(createGridColumns(entityDefinition, translations))
);

export const getSubGridByGridId = createSelector(
  [getFormModel, getComponentProps],
  (formModel, { id }) =>
    formModel[id].childrenNodes?.find(
      (node) => formModel[node].controlType === SS_CONTROL_TYPE.SUB_GRID
    )
);

export const getHiddenColumnsMap = createSelector(
  [getColumnsModel, getSsContext],
  (columnsModels, ssContext) => {
    const hiddenColumnsMap = {};

    columnsModels.forEach((column) => {
      const hiddenConfig = column.hidden;
      const columnId = column.controlId;

      if (_.isNil(hiddenConfig)) {
        hiddenColumnsMap[columnId] = false;
        return;
      }

      if (typeof hiddenConfig === 'string' || typeof hiddenConfig === 'boolean') {
        hiddenColumnsMap[columnId] = isTrue(hiddenConfig);
        return;
      }

      const evaluatedConfig = evaluateConditions({
        conditions: hiddenConfig.Conditions,
        ssContext,
      });

      hiddenColumnsMap[columnId] = evaluatedConfig;
    });

    return hiddenColumnsMap;
  }
);

export const getSortData = createSelector(
  [getEntityDefinition, getComponentProps],
  (entityDefinition, { sort }) => {
    if (!sort) {
      return;
    }
    const { direction, field = '' } = sort[0];
    const dataType = getPropType(entityDefinition, field, 'DataType');

    return { direction: direction?.toLowerCase(), dataType, accessor: field.split('.')[1] };
  }
);

export const isHighVolumeGrid = createSelector(
  [getWorkflowInfo, getEntityDefinition, getComponentProps],
  (workflowInfo, entityDefinition, { entityName }) => {
    const { itmId } = workflowInfo;
    const relationshipKey = getRelationShipKey(entityDefinition, entityName);

    return (
      relationshipKey === '1TOMANYDEFERCONTENTROCESSING' && _.isString(itmId) && itmId.length > 0
    ); // itmId should be non empty id (so, it isn't initiation page)
  }
);

export const getRowIdFromEntityData = createSelector(
  [getEntityData, getEntityDefinition, getPrimaryKeysMap, getComponentProps],
  (
    entityData,
    entityDefinition,
    primaryKeysMap,
    { entityName, index, isChildAttachment = false }
  ) => {
    if (entityName === 'V_ENU_NONPERSIS_USER_DATA') {
      return entityName;
    }

    const entityType = Object.keys(entityDefinition)[0];

    if (entityType === entityName) {
      return 'root';
    }

    const path = getDataPath(entityName, index, entityDefinition);
    const row = _.get(entityData, path);
    const primaryKey = primaryKeysMap[entityName];
    const newRowKey = isChildAttachment ? '_tempAttachmentId' : '_newRowId';

    return row?.[primaryKey] ?? row?.[newRowKey];
  }
);
